import React, { useState } from 'react';
import { allowedHeaderOnTabletView, getTableHeaders } from '../../data/tableHeaders';
import DataTable, { SortDirectionEnum, TSortDirection, TTableColumn } from '@/components/DataTable';
import { TTransactionHistory } from '../../types/TransactionHistory';
import { TTransactionGeneralLedgerCode } from '@/context/types/Business';
import TransactionHistoryTableRow from './TransactionHistoryTableRow';
import { TCustomer } from '@/context/types/Customer';
import { AccountType } from '@/context/types/Account';

const DATE_COLUMN = 'Date';

interface TransactionHistoryTableProps {
  onExpandDetails: (transaction: TTransactionHistory) => void;
  onSortDateColumn: () => void;
  onPageChange: (p: number) => void;
  currentPageItems: TTransactionHistory[];
  transactionsCount: number;
  currentPage: number;
  maxItemsPerPage: number;
  sortDirection: TSortDirection;
  currentGeneralLedgerCodes: TTransactionGeneralLedgerCode[];
  archivedGeneralLedgerCodes: TTransactionGeneralLedgerCode[];
  customer: TCustomer | undefined;
  accountType: AccountType | undefined;
}

const TransactionHistoryTable: React.FC<TransactionHistoryTableProps> = ({
  currentPageItems,
  currentPage,
  maxItemsPerPage,
  onSortDateColumn,
  transactionsCount,
  sortDirection,
  onExpandDetails,
  onPageChange,
  currentGeneralLedgerCodes,
  archivedGeneralLedgerCodes,
  customer,
  accountType
}) => {
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const hasGLCodes = currentPageItems.some((transaction) => transaction.details?.generalLedgerCode);

  const handleRowClick = (transaction: TTransactionHistory) => {
    setSelectedTransactionId(transaction.data.id);
    onExpandDetails(transaction);
  };

  const columns: TTableColumn<TTransactionHistory>[] = getTableHeaders(
    accountType!,
    hasGLCodes
  ).map((header) => ({
    key: header.label,
    header: header.label,
    canSort: header.canSort,
    hideOnSmallScreens: !allowedHeaderOnTabletView.includes(header.label)
  }));

  return (
    <DataTable<TTransactionHistory>
      columns={columns}
      data={currentPageItems}
      getRowKey={(transaction) => transaction.data.id}
      itemLabel="Transactions"
      emptyMessage="You currently have no transactions."
      pagination={{
        mode: 'server',
        currentPage,
        pageSize: maxItemsPerPage,
        totalItemsCount: transactionsCount,
        onPageChange
      }}
      sorting={{
        mode: 'server',
        sort: { key: DATE_COLUMN, direction: sortDirection ?? SortDirectionEnum.DESC },
        onSortChange: onSortDateColumn
      }}
      renderRow={(transaction, index) => (
        <TransactionHistoryTableRow
          key={transaction.data.id}
          index={index}
          accountType={accountType}
          customer={customer}
          transaction={transaction}
          hasGLCodes={hasGLCodes}
          currentGeneralLedgerCodes={currentGeneralLedgerCodes}
          archivedGeneralLedgerCodes={archivedGeneralLedgerCodes}
          onClickDetails={handleRowClick}
          isSelected={selectedTransactionId === transaction.data.id}
        />
      )}
    />
  );
};

export default TransactionHistoryTable;
